import { createElement, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity, ArrowRight, Bell, BrainCircuit, Check, ChevronRight, Clapperboard,
  Cloud, Film, FolderKanban, Gauge, Home, Link2, Menu, Plus, Radio, Search,
  Settings, ShieldCheck, Sparkles, Upload, WandSparkles, X
} from "lucide-react";
import { createProject, getDashboard, getPlatformStatus, getProjects, uploadSource } from "./api";
import ReviewWorkspace from "./ReviewWorkspace";

const demoProjects = [
  { id: "demo-1", title: "Conferencia ventas consultivas", sourceType: "youtube", status: "ready", clips: 12, progress: 100, updatedAt: "Hoy" },
  { id: "demo-2", title: "Liderazgo que transforma", sourceType: "upload", status: "processing", clips: 6, progress: 68, updatedAt: "Hace 18 min" },
  { id: "demo-3", title: "Innovación y cultura", sourceType: "drive", status: "draft", clips: 0, progress: 0, updatedAt: "Ayer" }
];

const navItems = [
  [Home, "Inicio", true], [WandSparkles, "Crear", false], [Upload, "Importar", false],
  [FolderKanban, "Proyectos", false], [Film, "Clips", false], [Settings, "Ajustes", false]
];

const statusLabels = { ready: "Listo", processing: "En producción", draft: "Borrador" };

function Logo() {
  return <div className="brand"><strong>CHM</strong><i /><span>SPEAKERS<br />CLIPS</span></div>;
}

function StatusDot({ state }) {
  return <span className={`status-dot status-${state}`} aria-label={state} />;
}

function formatUpdatedAt(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return value || "Ahora";
  const days = Math.max(-30, Math.min(0, Math.round((timestamp - Date.now()) / 86_400_000)));
  return new Intl.RelativeTimeFormat("es", { numeric: "auto" }).format(days, "day");
}

function NewProjectModal({ onClose }) {
  const queryClient = useQueryClient();
  const [sourceType, setSourceType] = useState("youtube");
  const [title, setTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const mutation = useMutation({
    mutationFn: async (input) => {
      const created = await createProject(input);
      if (sourceType === "upload") await uploadSource(created.project.id, file, setUploadProgress);
      return created;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["projects"] }); onClose(); }
  });

  function submit(event) {
    event.preventDefault();
    if (sourceType === "upload" && !file) return;
    mutation.mutate({ title, sourceType, sourceUrl: sourceType === "upload" ? null : sourceUrl });
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="new-project-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="icon-button modal-close" onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        <p className="eyebrow">NUEVA PRODUCCIÓN</p>
        <h2 id="new-project-title">Importa tu conferencia</h2>
        <p className="muted">Tu material viaja directo al almacenamiento privado y queda listo para el pipeline audiovisual.</p>
        <form onSubmit={submit}>
          <div className="source-tabs">
            {[["youtube", Link2, "Enlace"], ["drive", Cloud, "Drive"], ["upload", Upload, "Archivo"]].map(([value, Icon, label]) => (
              <button key={value} type="button" className={sourceType === value ? "active" : ""} onClick={() => setSourceType(value)}>
                {createElement(Icon, { size: 17 })}{label}
              </button>
            ))}
          </div>
          <label>Nombre del proyecto<input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ej. Conferencia Liderazgo 2026" required maxLength={120} /></label>
          {sourceType !== "upload" ? (
            <label>Enlace<input type="url" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://" required /></label>
          ) : (
            <label className="file-picker">
              <Upload size={22} />
              <span><strong>{file ? file.name : "Selecciona un video"}</strong><small>{file ? `${(file.size / 1_048_576).toFixed(1)} MB` : "MP4, MOV, MKV o WebM · hasta 5 GB"}</small></span>
              <input type="file" accept="video/mp4,video/quicktime,video/x-matroska,video/webm,.mp4,.mov,.mkv,.webm" onChange={(event) => setFile(event.target.files?.[0] || null)} required />
            </label>
          )}
          {mutation.isPending && sourceType === "upload" && <div className="upload-progress" aria-label={`Carga ${uploadProgress}%`}><i style={{ width: `${uploadProgress}%` }} /><span>{uploadProgress ? `Subiendo ${uploadProgress}%` : "Preparando carga segura..."}</span></div>}
          {mutation.error && <p className="form-error">{mutation.error.message}</p>}
          <button className="primary wide" disabled={mutation.isPending}>{mutation.isPending ? (uploadProgress ? `Subiendo ${uploadProgress}%` : "Preparando...") : "Comenzar producción"}<ArrowRight size={17} /></button>
        </form>
      </section>
    </div>
  );
}

function ProjectCard({ project, index, onOpen }) {
  const updated = formatUpdatedAt(project.updatedAt);
  const state = statusLabels[project.status] || "En revisión";
  return (
    <article className={`project-card project-visual-${index % 4}`}>
      <div className="project-thumb">
        {index === 0 && <img src="/media/chm-showcase-poster-v1.jpg" alt="" />}
        <span className="project-index">0{index + 1}</span>
        <span className="project-duration">{project.clips ? `${project.clips} CLIPS` : `${project.progress || 0}%`}</span>
      </div>
      <div className="project-copy">
        <div className="project-meta"><span><StatusDot state={project.status} />{state}</span><small>{updated}</small></div>
        <h3>{project.title}</h3>
        <p>{project.clips ? `${project.clips} reels listos para revisar` : project.moments ? `${project.moments} momentos detectados` : project.sourceVerified ? "Fuente verificada" : "Esperando producción"}</p>
        <div className="progress"><i style={{ width: `${project.progress || 0}%` }} /></div>
      </div>
      <button className="project-open" aria-label={`Abrir ${project.title}`} onClick={() => onOpen(project.id)}><ArrowRight size={18} /></button>
    </article>
  );
}

function PlatformPulse({ platform, checking, error }) {
  const services = [["API", platform.api], ["DATA", platform.database], ["QUEUE", platform.redis], ["STORAGE", platform.objectStorage]];
  const online = services.filter(([, state]) => state === "ready").length;
  return (
    <section className="platform-pulse">
      <div className="pulse-title"><span><Radio size={15} /> PRODUCCIÓN EN VIVO</span><strong>{error ? "Modo parcial" : checking ? "Verificando" : `${online}/4 sistemas`}</strong></div>
      <div className="service-pills">{services.map(([label, value]) => <span key={label}><StatusDot state={value === "ready" ? "ready" : value === "checking" ? "processing" : "draft"} />{label}</span>)}</div>
      <div className="cost-guard"><ShieldCheck size={17} /><span><strong>Cost Guard</strong><small>Presupuesto y timeout protegidos</small></span></div>
    </section>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const statusQuery = useQuery({ queryKey: ["status"], queryFn: getPlatformStatus });
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: getProjects });
  const dashboardQuery = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard, refetchInterval: 5000 });
  const projects = projectsQuery.data?.projects || (import.meta.env.DEV ? demoProjects : []);
  const platform = statusQuery.data?.platform || { api: "checking", database: "checking", redis: "checking", objectStorage: "checking" };
  const metrics = dashboardQuery.data?.metrics || { projects: 0, verifiedSources: 0, activeJobs: 0, completedJobs: 0, candidateMoments: 0, monthCostUsd: 0 };
  const primaryProject = projects[0];
  const reviewMatch = location.pathname.match(/^\/projects\/([^/]+)\/review\/?$/);
  const reviewProjectId = reviewMatch ? decodeURIComponent(reviewMatch[1]) : null;

  return (
    <div className="app-shell">
      <aside className={mobileNav ? "sidebar open" : "sidebar"}>
        <Logo />
        <p className="nav-label">ESTUDIO</p>
        <nav>{navItems.map(([Icon, label, active]) => <button className={!reviewProjectId && active ? "active" : ""} key={label} onClick={() => label === "Inicio" && navigate("/")}>{createElement(Icon, { size: 18 })}<span>{label}</span>{!reviewProjectId && active && <i />}</button>)}</nav>
        <div className="sidebar-spacer" />
        <div className="studio-card"><span className="studio-number">01</span><div><small>CHM STUDIO</small><strong>Producción global</strong></div><ChevronRight size={16} /></div>
        <div className="profile"><div className="avatar">CH</div><span><strong>Equipo CHM</strong><small>Workspace principal</small></span><span className="profile-status" /></div>
      </aside>

      <main>
        {reviewProjectId ? <ReviewWorkspace projectId={reviewProjectId} onBack={() => navigate("/")} /> : <>
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileNav(!mobileNav)} aria-label="Menú"><Menu size={20} /></button>
          <div className="welcome"><p className="eyebrow">CHM CONTENT STUDIO <span>•</span> LIVE WORKSPACE</p><h1>Buenos días, equipo CHM.</h1></div>
          <div className="header-actions"><button className="icon-button notification" aria-label="Notificaciones"><Bell size={18} /><i /></button><button className="search-button"><Search size={17} /><span>Buscar</span><kbd>⌘ K</kbd></button><button className="primary" onClick={() => setModalOpen(true)}><Plus size={18} />Nueva producción</button></div>
        </header>

        <section className="cinema-hero">
          <div className="cinema-stage" />
          <div className="film-grain" />
          <div className="hero-copy">
            <div className="premiere-pill"><span /> IA AUDIOVISUAL · PIPELINE ACTIVO</div>
            <h2>Tu escenario.<br /><em>Ahora en vertical.</em></h2>
            <p>Convertimos conferencias extraordinarias en historias breves que posicionan, conectan y mueven audiencias.</p>
            <div className="hero-actions"><button className="primary hero-primary" onClick={() => setModalOpen(true)}><Clapperboard size={18} />Crear clips</button><button className="secondary" onClick={() => setModalOpen(true)}><Link2 size={17} />Importar enlace</button></div>
            <div className="hero-proof">
              <span><strong>{metrics.candidateMoments}</strong><small>momentos detectados</small></span>
              <i />
              <span><strong>{metrics.completedJobs}</strong><small>etapas completadas</small></span>
              <i />
              <span><strong>DEMO</strong><small>reel de muestra</small></span>
            </div>
          </div>

          <div className="reel-showcase">
            <div className="reel-orbit orbit-one" /><div className="reel-orbit orbit-two" />
            <div className="reel-shell">
              <div className="reel-toolbar"><span>CHM / REEL MUESTRA</span><small>00:31</small></div>
              <video autoPlay muted loop playsInline poster="/media/chm-showcase-poster-v1.jpg" aria-label="Reel creado por CHM Speakers Clips">
                <source src="/media/chm-showcase-reel-v1.mp4" type="video/mp4" />
              </video>
              <div className="reel-scan" />
            </div>
            <div className="score-float"><span><Sparkles size={13} /> POTENCIAL</span><strong>91</strong><small>/100</small></div>
            <div className="ready-float"><Check size={14} /><span><strong>Demostración premium</strong><small>9:16 · 1080p · subtítulos</small></span></div>
          </div>

          <div className="hero-number">01</div>
        </section>

        <section className="production-ribbon">
          <div className="ribbon-intro"><Sparkles size={16} /><span><small>PIPELINE CREATIVO</small><strong>{primaryProject?.title || "Nueva conferencia"}</strong></span></div>
          {[["01", "Importación", metrics.verifiedSources > 0], ["02", "Inteligencia", metrics.candidateMoments > 0], ["03", "Encuadre", metrics.completedJobs > 0], ["04", "Entrega", metrics.completedJobs > 0]].map(([number, label, done], index) => (
            <div className={`pipeline-step ${done ? "done" : ""}`} key={label}><span>{done ? <Check size={13} /> : number}</span><strong>{label}</strong>{index < 3 && <i />}</div>
          ))}
          <button onClick={() => setModalOpen(true)}>Ver producción <ArrowRight size={15} /></button>
        </section>

        <section className="intelligence-grid">
          <div className="intelligence-heading"><p className="eyebrow">INTELIGENCIA DE PRODUCCIÓN</p><h2>Lo importante, sin ruido.</h2><p>Señales claras para tomar decisiones editoriales y publicar con confianza.</p></div>
          <article className="insight-card accent-orange"><div><BrainCircuit size={20} /><span>Selección editorial</span></div><strong>{metrics.candidateMoments}</strong><p>momentos con potencial comercial</p><small>Analizados por semántica, gancho y cierre</small></article>
          <article className="insight-card"><div><Gauge size={20} /><span>Operación cloud</span></div><strong>{metrics.activeJobs}</strong><p>jobs activos ahora</p><small>{metrics.completedJobs} etapas completadas sin intervención</small></article>
          <article className="insight-card"><div><Cloud size={20} /><span>Uso del mes</span></div><strong>${metrics.monthCostUsd.toFixed(2)}</strong><p>costo de procesamiento</p><small>Visibilidad financiera por proyecto</small></article>
        </section>

        <section className="workspace-grid">
          <div className="projects-section">
            <div className="section-heading"><div><p className="eyebrow">FILM ROOM</p><h2>Producciones recientes</h2></div><button>Ver archivo <ArrowRight size={15} /></button></div>
            {projects.length ? <div className="project-list">{projects.slice(0, 4).map((project, index) => <ProjectCard project={project} index={index} onOpen={(projectId) => navigate(`/projects/${encodeURIComponent(projectId)}/review`)} key={project.id} />)}</div> : <div className="empty-projects"><Clapperboard size={25} /><div><strong>Tu primera producción comienza aquí</strong><p>Importa una conferencia o pega un enlace para activar el pipeline audiovisual.</p></div><button className="primary" onClick={() => setModalOpen(true)}><Plus size={16} />Nueva producción</button></div>}
          </div>
          <PlatformPulse platform={platform} checking={statusQuery.isLoading} error={statusQuery.isError} />
        </section>
        </>}
      </main>
      {modalOpen && <NewProjectModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}
